import type { QueryClient } from "@tanstack/react-query";
import { Link, Outlet, createRootRouteWithContext, useRouter } from "@tanstack/react-router";
import { useStaleChunkReload } from "@seagames/ui";
import { useTranslation } from "react-i18next";
import { SessionUnreachable } from "../components/auth/session-screens";
import { Button } from "../components/ui/button";
import { errorMessage, isTransientError, supportRequestIdOf } from "../lib/api/messages";
import { isSessionUnreachable } from "../lib/auth/session-check";

export interface RouterContext {
	queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
	component: () => <Outlet />,
	notFoundComponent: NotFound,
	errorComponent: RouteError,
});

function Frame({ children }: { children: React.ReactNode }) {
	return (
		<div className="min-h-dvh bg-paper">
			<div className="h-[0.375rem] bg-board" />
			<main className="mx-auto flex max-w-[40rem] flex-col items-start gap-[1rem] px-[1.25rem] py-[3rem]">{children}</main>
		</div>
	);
}

function NotFound() {
	const { t } = useTranslation();
	return (
		<Frame>
			<h1 className="font-black text-[1.75rem]">{t("notFound.title")}</h1>
			<p className="text-muted">{t("notFound.body")}</p>
			<Link to="/" className="font-bold text-board underline underline-offset-4">
				{t("notFound.home")}
			</Link>
		</Frame>
	);
}

/**
 * A route that threw. No answer (network / 503) gets "try again" and KEEPS
 * the session — never "sign in again".
 */
function RouteError({ error, reset }: { error: Error; reset: () => void }) {
	// A chunk removed by a deploy: the page is reloading to the new version.
	if (useStaleChunkReload(error)) return null;
	// The session check got no answer: the band + a screen that retries by itself.
	if (isSessionUnreachable(error)) return <SessionUnreachable reset={reset} />;
	return <GenericRouteError error={error} reset={reset} />;
}

function GenericRouteError({ error, reset }: { error: Error; reset: () => void }) {
	const { t } = useTranslation();
	const router = useRouter();
	const transient = isTransientError(error);
	const requestId = supportRequestIdOf(error);
	return (
		<Frame>
			<h1 className="font-black text-[1.75rem]">{transient ? t("routeError.unreachable") : t("routeError.title")}</h1>
			<p className="text-muted">{errorMessage(error)}</p>
			{requestId && (
				<p className="text-[0.8125rem] text-muted">
					{t("routeError.requestId")} <code className="font-mono">{requestId}</code>
				</p>
			)}
			<div className="flex gap-[0.5rem]">
				<Button
					onClick={() => {
						reset();
						void router.invalidate();
					}}
				>{t("actions.retry")}</Button>
				<Link to="/" className="inline-flex min-h-touch items-center px-[1rem] font-bold text-board">
					{t("notFound.home")}
				</Link>
			</div>
		</Frame>
	);
}
